import type { AppDispatch, State } from '~/store';
import { selectCurrentWorkflowHistoryEntry, selectWorkflowData, selectWorkflowProps } from '../selectors';
import { appendWorkflowHistoryEntry, applyHistoryTraversal } from '../slice';
import type { UiEffectContext, WorkflowHistoryEntry, WorkflowStateChange } from '../types';
import type { Operation } from 'flow/service/changelog/types';
import { diffWorkflowState, isUndo } from 'flow/service/history';
import type { SchemaInstance, WorkflowProps } from 'flow/store/engine/types';
import { getSnackbarMessage } from 'flow/editor/hooks/useWorkflowHistory/helpers';
import type { HistoryAction } from 'flow/service/history/constants';
import type { ISnackBar } from '@usealloy/component-library';

function cloneWorkflowState(state: State) {
  return {
    workflowData: structuredClone(selectWorkflowData(state)) as Partial<SchemaInstance>,
    workflowProps: structuredClone(selectWorkflowProps(state)) as WorkflowProps,
  };
}

export function createWorkflowHistoryThunk(
  /** A callback that mutates redux state by performing its own dispatch(es). */
  stateChange: WorkflowStateChange,
  isWorkflowHistoryOn: boolean,
  context: {
    operation: Operation;
    uiEffectContext: UiEffectContext;
  },
) {
  return (dispatch: AppDispatch, getState: () => State) => {
    if (!isWorkflowHistoryOn) {
      stateChange();
      return;
    }

    const { operation, uiEffectContext } = context;

    const beforeState = cloneWorkflowState(getState());

    stateChange();

    const afterState = cloneWorkflowState(getState());

    const diff = diffWorkflowState(beforeState, afterState);

    if (diff.workflowData === undefined) {
      // no-op change, do not append a history entry
      return;
    }

    const entry: WorkflowHistoryEntry = {
      diff: {
        workflowData: diff.workflowData,
        workflowProps: diff.workflowProps ?? null,
      },
      operation,
      uiEffectContext,
    };

    dispatch(appendWorkflowHistoryEntry({ entry }));
  };
}

export function traverseHistoryThunk({
  historyAction,
  addSnackbar,
  triggerUiEffect,
}: {
  historyAction: HistoryAction;
  addSnackbar: (snackBar: ISnackBar) => void;
  triggerUiEffect: (
    workflowHistoryEntry: WorkflowHistoryEntry,
    historyAction: HistoryAction,
  ) => void;
}) {
  return (dispatch: AppDispatch, getState: () => State) => {
    const undoEntry = selectCurrentWorkflowHistoryEntry(getState());

    dispatch(applyHistoryTraversal({ historyAction }));

    const redoEntry = selectCurrentWorkflowHistoryEntry(getState());

    const impactedEntry = isUndo(historyAction) ? undoEntry : redoEntry;

    addSnackbar({
      title: getSnackbarMessage(historyAction, impactedEntry),
      type: 'success',
    });

    triggerUiEffect(impactedEntry, historyAction);
  };
}
